import type { CategoriaResult } from './types'

export interface BonusResultForTable {
  categorias: CategoriaResult[]
  scoreGeral: number
  totalSalarios: number
  totalMonetario: number
}

export interface BonusSummaryTableProps {
  bonusResult: BonusResultForTable
  salarioBase: number
  /** GAP 5: When provided, only show published categories for director view */
  publicados?: Record<string, boolean>
  /** When true, show all categories regardless of publish state (RH view) */
  isRH?: boolean
}

const formatBRL = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

export function BonusSummaryTable({ bonusResult, salarioBase, publicados, isRH }: BonusSummaryTableProps) {
  // Filter categories based on publish state (directors only see published)
  const visibleCategorias = isRH || !publicados
    ? bonusResult.categorias
    : bonusResult.categorias.filter(cat => publicados[cat.id] === true)

  const totalVisible = visibleCategorias.length
  const totalAll = bonusResult.categorias.length
  const isPartial = totalVisible < totalAll && !isRH

  // Compute partial totals
  const partialSalarios = visibleCategorias.reduce((sum, c) => sum + c.bonusSalarios, 0)
  const partialMonetario = salarioBase > 0 ? partialSalarios * salarioBase : 0

  return (
    <div className="table-container" data-testid="bonus-summary-table">
      <table className="table">
        <thead>
          <tr className="table-header">
            <th className="text-left">Categoria</th>
            <th className="text-right">Peso</th>
            <th className="text-right">Média</th>
            <th className="text-right">Salários</th>
            <th className="text-right">Status</th>
          </tr>
        </thead>
        <tbody>
          {visibleCategorias.map((cat, index) => (
            <tr key={cat.id} className={`table-row ${index % 2 === 1 ? 'bg-white/[0.02]' : ''}`} data-testid={`bonus-summary-row-${cat.id}`}>
              <td className="text-white">{cat.nome}</td>
              <td className="text-right text-brand-gray">{cat.peso}%</td>
              <td className="text-right text-brand-gray">
                {cat.gateBlocked ? '—' : `${cat.media.toFixed(1)}%`}
              </td>
              <td className="text-right text-white">{cat.bonusSalarios.toFixed(2)}</td>
              <td className="text-right">
                {cat.gateBlocked ? (
                  <span className="text-brand-red font-semibold">BLOQ</span>
                ) : (
                  <span className="text-green-500">Prop.</span>
                )}
              </td>
            </tr>
          ))}

          {/* Total row */}
          <tr className="table-row-financial font-bold border-t-2 border-brand-primary/20" data-testid="bonus-summary-total">
            <td className="text-white text-base">
              TOTAL
              {isPartial && (
                <span className="text-xs text-brand-gray font-normal ml-2">
                  (parcial — {totalVisible} de {totalAll} categorias publicadas)
                </span>
              )}
            </td>
            <td />
            <td />
            <td className="text-right text-brand-primary">
              {isRH ? bonusResult.totalSalarios.toFixed(2) : partialSalarios.toFixed(2)} sal.
            </td>
            <td className="text-right text-brand-primary">
              {formatBRL(isRH ? bonusResult.totalMonetario : partialMonetario)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
